/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { ActionCancelJob, Job, ProcessDescription } from 'ogc-processes-components';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';
import { Braces } from 'lucide-react';
import { Column, ColumnProps } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { ButtonGroup } from 'primereact/buttongroup';
import { Tooltip } from 'primereact/tooltip';

interface Props {
  className?: string;
  job: Job;
  extraExecutionContent?: Array<{ name: string; value: string }>;
  process?: ProcessDescription;
  onRetryJob?: () => void;
  handleCancelJob?: () => Promise<Job>;
}
export const ExecutionContent: React.FC<Props> = (props) => {
  const { t } = useTranslation();

  const { className, job, process, extraExecutionContent, onRetryJob, handleCancelJob } = props;
  const [showCancelJobAlert, setShowCancelJobAlert] = useState(false);

  const inputsWithTitleAreExist = useMemo(() => {
    return Object.entries(process?.inputs || {}).some(([k, v]) => {
      return !!v.title && v.title !== '' && v.title !== k;
    });
  }, [process?.inputs]);

  const findInputLabel = useCallback(
    (key: string) => {
      if (inputsWithTitleAreExist) {
        const input = process?.inputs?.[key];
        if (input && input.title && input.title !== key) {
          return input.title;
        }
      }
      return key;
    },
    [inputsWithTitleAreExist, process?.inputs]
  );

  const secretInputs = useMemo(() => {
    const secretInputs: string[] = [];
    Object.entries(process?.inputs || {}).forEach(([k, v]) => {
      if (v.schema.format === 'secret') {
        secretInputs.push(k);
      }
    });
    return secretInputs;
  }, [process?.inputs]);

  const exportJOBInputs = (job: Job) => {
    const jsonString = JSON.stringify(job, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = job.processID.concat('.json');
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const defaultColumns: ColumnProps[] = useMemo(() => {
    const columns = [
      {
        field: 'name',
        header: t(['misc.inputs', 'title']),
        style: { width: '20%' },
        body: (rowData: { name: string; value: string; isSecret: boolean }) => {
          if (rowData.isSecret) {
            return (
              <span className="inline-flex align-items-center gap-2">
                <i className="pi pi-lock text-color-secondary" />
                <span>{rowData.name}</span>
                <i
                  className="pi pi-info-circle secret-info-tooltip cursor-pointer text-color-secondary"
                  data-pr-tooltip={t(['jobs.secret_from_vault', 'This value is a secret stored in the AntFlow vault.'])}
                />
              </span>
            );
          }
          return <span>{rowData.name}</span>;
        }
      },
      { field: 'value', header: '' }
    ];
    return columns;
  }, [t]);

  const IOArray = useMemo(() => {
    if (job.execution_content) {
      const ogcExecutionContent = Object.entries(job.execution_content).map(([k, v]) => {
        return { name: findInputLabel(k), value: JSON.stringify(v), isSecret: secretInputs.includes(k) };
      });
      return extraExecutionContent
        ? ogcExecutionContent.concat(extraExecutionContent.map((item) => ({ ...item, isSecret: false })))
        : ogcExecutionContent;
    }
    return [];
  }, [job.execution_content, extraExecutionContent, findInputLabel, secretInputs]);

  return (
    <div className={classNames(className, 'w-full')}>
      <div className="flex justify-content-between align-items-start mb-3">
        <div className="mb-3 flex align-items-center ">
          <Braces size={20} className={'mr-2'} />
          <span className="text-xl font-medium ">{t(['jobs.properties.execution_content', 'Execution Content'])}</span>
        </div>
        <ButtonGroup>
          <Button
            severity="info"
            outlined
            size="small"
            onClick={() => exportJOBInputs(job)}
            label={t(['actions.export_job_inputs', 'export'])}
            icon="pi pi-download"
            iconPos="left"
          />
          {['failed', 'successful', 'finished', 'dismissed'].includes(job.status) && !!process && (
            <Button
              severity="success"
              size="small"
              onClick={onRetryJob}
              label={t(['actions.retry', 'Retry'])}
              icon="pi pi-play"
            />
          )}

          {['accepted', 'created'].includes(job.status) && !!process && (
            <Button
              severity="danger"
              size="small"
              onClick={() => setShowCancelJobAlert(true)}
              label={t(['actions.cancel', 'Cancel'])}
              icon="pi pi-times"
            />
          )}
        </ButtonGroup>
      </div>

      {handleCancelJob && (
        <ActionCancelJob
          job={job}
          visible={showCancelJobAlert}
          setVisible={setShowCancelJobAlert}
          handleCancelJob={() => {
            return handleCancelJob().then((res) => {
              setShowCancelJobAlert(false);
              return res;
            });
          }}
        />
      )}

      <DataTable
        width={100}
        size="small"
        scrollable
        className="text-md"
        showHeaders={false}
        emptyMessage={t(['misc.404', 'No available data'])}
        scrollHeight="300px"
        value={[...IOArray]}
        tableStyle={{ width: '100%' }}
      >
        {defaultColumns.map((c) => (
          <Column key={c.field} {...c} />
        ))}
      </DataTable>
      <Tooltip className="text-xs" target=".secret-info-tooltip" />
    </div>
  );
};
